import { useState, useCallback, useEffect, useRef } from 'react';
import type { AnswerValue } from '../types';
import { safeLocalStorage } from '../lib/safeStorage';

const STORAGE_KEY_PREFIX = 'quiz_progress_';
const PERSIST_DEBOUNCE_MS = 250;

function getStorageKey(sessionId: string, partner: string): string {
  return `${STORAGE_KEY_PREFIX}${sessionId}_${partner}`;
}

function loadAnswers(key: string): Record<string, AnswerValue> {
  try {
    const raw = safeLocalStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, string>;
    const migrated: Record<string, AnswerValue> = {};
    for (const [questionId, value] of Object.entries(parsed)) {
      // 'hate' was removed in the 5→4 scale consolidation; map to 'dislike'
      migrated[questionId] = (value === 'hate' ? 'dislike' : value) as AnswerValue;
    }
    return migrated;
  } catch {
    // JSON.parse failure — treat as no saved progress
    return {};
  }
}

export function useQuizProgress(sessionId: string, partner: string) {
  const storageKey = getStorageKey(sessionId, partner);
  const [answers, setAnswersState] = useState<Record<string, AnswerValue>>(() => loadAnswers(storageKey));
  const answersRef = useRef(answers);
  const persistTimeoutRef = useRef<number | null>(null);
  const hasPendingPersistRef = useRef(false);

  const cancelPendingPersist = useCallback(() => {
    if (persistTimeoutRef.current !== null) {
      window.clearTimeout(persistTimeoutRef.current);
      persistTimeoutRef.current = null;
    }
    hasPendingPersistRef.current = false;
  }, []);

  const flushPersist = useCallback(() => {
    if (!hasPendingPersistRef.current) return;
    safeLocalStorage.setItem(storageKey, JSON.stringify(answersRef.current));
    hasPendingPersistRef.current = false;
    if (persistTimeoutRef.current !== null) {
      window.clearTimeout(persistTimeoutRef.current);
      persistTimeoutRef.current = null;
    }
  }, [storageKey]);

  const schedulePersist = useCallback(() => {
    hasPendingPersistRef.current = true;
    if (persistTimeoutRef.current !== null) {
      window.clearTimeout(persistTimeoutRef.current);
    }
    persistTimeoutRef.current = window.setTimeout(() => {
      flushPersist();
    }, PERSIST_DEBOUNCE_MS);
  }, [flushPersist]);

  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flushPersist();
      }
    };

    window.addEventListener('pagehide', flushPersist);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', flushPersist);
      flushPersist();
    };
  }, [flushPersist]);

  const setAnswer = useCallback(
    (questionId: string, value: AnswerValue) => {
      setAnswersState((prev) => {
        const next = { ...prev, [questionId]: value };
        answersRef.current = next;
        schedulePersist();
        return next;
      });
    },
    [schedulePersist],
  );

  const clearProgress = useCallback(() => {
    cancelPendingPersist();
    answersRef.current = {};
    safeLocalStorage.removeItem(storageKey);
    setAnswersState({});
  }, [cancelPendingPersist, storageKey]);

  return { answers, setAnswer, clearProgress };
}
